import React, { useState, useMemo, useCallback, useRef } from 'react';
import { isEmpty, noop } from 'lodash';
import classnames from 'classnames';
import pathfinder from '@fiverr-private/pathfinder';
import { useBreakpoints, useEnteredView } from '@fiverr-private/hooks';
import {
    ModalType,
    openFipMigrationModal,
    shouldDisplayMigrationPopup,
    FLOW_CONTEXT_TYPES,
} from '@fiverr-private/pro_migration';
import { useSuccessSignCtaEvent, BUSINESS_TRIGGER_CTA } from '@fiverr-private/business_success_sign_modal';
import { logger } from '@fiverr-private/obs';
import { theme } from '@fiverr-private/theme';
import { ContactSellerButton } from '@fiverr-private/send_message';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container } from '@fiverr-private/layout_components';
import { contactMeClicked } from '../../../utils/triggerGtmEvent';
import { useMonitoring } from '../../../utils/monitoring/useMonitoring';
import { useSellerVacation } from '../../../hooks/useSellerVacation';
import { raiseError } from '../../../utils/raiseError';
import { getWindow } from '../../../utils/getGlobals';
import { SafeWrapper } from '../../SafeWrapper/SafeWrapper';
import { LazyComponent } from '../../LazyComponent/LazyComponent';
import { UserActivationModalWrapper } from '../../UserActivationModalWrapper';
import { SUPPORTED_ACTIONS } from '../../../constants';
import { useBriefAiAvailablity } from '../useBriefAiAvailability';
import { useFloatingChatTrigger } from '../../../hooks/useFloatingChatTrigger';
import { useRelevantGigs } from '../useRelevantGigs';
import { handleSignUpModalOpen } from '../getGlobals';
import { useContactDataClick } from '../../ActionButtons/ContactButton/useContactDataClick';

import { ModalInitialData } from '../../../hooks/useContactDataQuery';
import { FederatedSeller } from '../../../types';
import { useIsSelfView } from '../../../hooks/useIsSelfView';
import { GigData } from '../../../server/types/GigData';
import { ContactButtonDisplayComponent } from './ContactButtonDisplayComponent';
import styles from './index.module.scss';
import { ContactOptions } from './ContactOptions';
import { isPendingUser } from './utils/isPendingUser';

const lazyImportModal = () => import(/* webpackChunkName: 'ContactSellerModal' */ './ContactSellerModal');
const lazyImportDrawer = () => import(/* webpackChunkName: 'ContactSellerDrawer' */ './ContactSellerDrawer');

export interface ContactButtonViewProps {
    className?: string;
    source: string;
    size?: 'sm' | 'md' | 'lg';
    aiBriefOnly?: boolean;
    buttonCopy?: React.ReactNode;
    contactButtonProps?: Partial<React.ComponentProps<typeof import('@fiverr-private/button')['Button']>>;
    renderTrigger?: (props?: { onClick?: () => void }) => React.ReactNode;
    disabled?: boolean;
    fullWidth?: boolean;
    fullHeight?: boolean;
    aiBriefInitialGenerationText?: string;
    shouldRenderFloatingBtn?: boolean;
    seller: FederatedSeller;
    renderDropdown?: boolean;
    isOnline?: boolean;
    gigs: GigData[];
    disableProMigration?: boolean;
}

const ContactButtonView = ({
    className,
    source,
    size,
    renderDropdown = false,
    aiBriefOnly = false,
    buttonCopy,
    contactButtonProps = {},
    renderTrigger,
    fullWidth = true,
    fullHeight,
    aiBriefInitialGenerationText,
    shouldRenderFloatingBtn,
    seller,
    isOnline,
    gigs,
    disableProMigration = false,
}: ContactButtonViewProps) => {
    const {
        experience: { isBusiness: isBusinessExperience },
        userId,
    } = getContext();

    const username = seller?.user?.name || '';
    const isActivationCompleted = seller?.user?.isActivationCompleted;
    const sellerId = seller?.user?.id;
    const isCurrentUser = useIsSelfView();
    const [showModal, setShowModal] = useState(false);
    const [activateUserModal, setRenderAndOpenUserActivationModal] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const mostRelevantGig = useRelevantGigs(gigs as GigData[])?.[0];
    const { isOnVacation } = useSellerVacation();
    const { md: isMD } = useBreakpoints();
    const isBriefAvailable = useBriefAiAvailablity(gigs as GigData[]);
    const { isFloatingChatEnabled, openFloatingChat } = useFloatingChatTrigger();
    const { fireImpressionEvents, fireEvents, Providers } = useMonitoring();

    const profilePhoto = seller?.user?.profileImageUrl;
    const responseTimeInHrs = seller?.responseTime?.inHours;
    const displayName = seller?.user?.profile?.displayName;

    useEnteredView(buttonRef, {
        threshold: 1,
        onEntered: () => {
            if (!shouldRenderDropdown) {
                fireImpressionEvents({
                    [Providers.BigQuery]: {
                        type: 'contact_me_button_shown',
                        element: {
                            type: 'contact_seller_only',
                            name: 'inbox',
                        },
                    },
                });
            }
        },
    });

    const conversationUrl = pathfinder('conversation', { id: username });
    const isVisible = buttonRef.current ? !!buttonRef.current?.offsetParent : false;
    const userHasPendingState = isPendingUser(isActivationCompleted);
    const {
        isLoading,
        modalInitialData,
        isContactClicked,
        setIsContactClicked,
        setEnableFetchData,
        refetchInitialData,
        isContactClickReady,
    } = useContactDataClick({
        username,
        isVisible,
        isLoggedIn: !!userId,
        isMD,
        userHasPendingState,
        onError: () => raiseError(),
    });

    const redirectToInbox = useCallback(() => {
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'redirected to inbox from contact button',
                source,
                buyerType: modalInitialData?.priorInteraction ? 'Repeated' : 'First time',
            },
        });
        getWindow()?.location.assign(conversationUrl);
    }, [conversationUrl, modalInitialData, source, fireEvents, Providers.Mixpanel]);

    const isLoadingIcon = isContactClicked && isLoading;
    const sellerName = seller?.user?.profile?.displayName || username;
    const emptyGigsList = isEmpty(gigs);

    const sendMixpanelClickContactEvent = (priorInteractions) => {
        fireEvents({
            [Providers.Mixpanel]: { type: 'clicked contact me button', source, priorInteraction: priorInteractions },
        });
    };

    const isContactDisabled = () => {
        if (isCurrentUser || emptyGigsList) {
            return true;
        } else if (isOnVacation) {
            return !seller?.vacation?.isContactAllowed;
        }
        return false;
    };

    const onContactOpen = useCallback(() => setShowModal(true), []);
    const onCloseActivateModal = useCallback(() => setRenderAndOpenUserActivationModal(false), []);

    const onContactClick = async () => {
        if (!isMD && typeof window !== 'undefined') {
            window.location.href = conversationUrl;
            return false;
        }

        setIsContactClicked(true);
        contactMeClicked();

        let data: ModalInitialData | undefined | void;
        try {
            data = await isContactClickReady();
        } catch (err: unknown) {
            logger.error('Failed to get isContactClickReady data', err as Error);
        }

        const hasPriorInteractions = modalInitialData?.priorInteraction || data?.priorInteraction;

        if (hasPriorInteractions) {
            redirectToInbox();
            return;
        }

        if (!userId) {
            return handleSignUpModalOpen({
                source,
                type: BUSINESS_TRIGGER_CTA.CONTACT,
                data: { sellerName },
                triggerType: 'blocked_action',
            });
        }

        if (userHasPendingState) {
            setRenderAndOpenUserActivationModal(true);
            return false;
        }

        if (isBusinessExperience && shouldDisplayMigrationPopup() && !disableProMigration) {
            openFipMigrationModal({
                variant: ModalType.FullMigration,
                payload: {
                    source,
                    biEnrichment: {
                        pageName: source,
                        pageElementName: 'contact_button',
                        trigger: 'contact',
                    },
                    flowContext: {
                        type: FLOW_CONTEXT_TYPES.contact,
                        sellerName,
                    },
                    redirectCtaData: {
                        source,
                        type: BUSINESS_TRIGGER_CTA.CONTACT,
                        data: { sellerName },
                    },
                },
            });
        } else if (isFloatingChatEnabled) {
            openFloatingChat();
        } else {
            onContactOpen();
        }
        sendMixpanelClickContactEvent(hasPriorInteractions);
    };

    useSuccessSignCtaEvent({
        source,
        type: BUSINESS_TRIGGER_CTA.CONTACT,
        callback: onContactClick,
    });

    const onMouseEnter = useCallback(() => setEnableFetchData(true), [setEnableFetchData]);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, [setShowModal]);

    const modalProps = useMemo(() => {
        const recipientInfo = modalInitialData?.recipient || {};
        const show = showModal && !isEmpty(modalInitialData);
        const displayName = seller?.user?.profile?.displayName;
        const { category_id: categoryId, sub_category_id: subCategoryId } = mostRelevantGig || {};
        return {
            pageName: source,
            isOpen: show,
            recipientInfo: {
                ...recipientInfo,
                sellerId,
                displayName,
            },
            attachmentsUploadConfig: modalInitialData?.attachmentsUploadConfig,
            showInfo: true,
            source: 'business_page_contact_me',
            onSubmitSuccess: refetchInitialData,
            onClose: handleCloseModal,
            withSatisfactionGuarantee: seller?.isPro && isBusinessExperience,
            ...(isBriefAvailable && categoryId && subCategoryId && { brief: { categoryId, subCategoryId } }),
            ...(seller.vacation && { vacation: seller.vacation }),
        };
    }, [
        refetchInitialData,
        isBriefAvailable,
        mostRelevantGig,
        modalInitialData,
        handleCloseModal,
        seller,
        sellerId,
        showModal,
        source,
        isBusinessExperience,
    ]);

    /** @type {ButtonProps} */
    const buttonProps = isMD
        ? { onClick: onContactClick, onMouseEnter }
        : {
              onClick: () => {
                  sendMixpanelClickContactEvent(modalInitialData?.priorInteraction);
                  redirectToInbox();
              },
          };

    const lazyImportLink = isBusinessExperience ? lazyImportDrawer : lazyImportModal;

    const shouldShowAiBriefButton = useBriefAiAvailablity(gigs as GigData[]);
    const shouldRenderDropdown = (renderDropdown && shouldShowAiBriefButton) || aiBriefOnly;

    const contactButtonClass = classnames(styles.contactButton, 'flex-center');

    const displayElement = renderTrigger ? (
        renderTrigger({ onClick: buttonProps.onClick })
    ) : (
        <ContactButtonDisplayComponent
            componentName="ContactButtonDisplayComponent"
            size={size}
            source={source}
            className={contactButtonClass}
            disabled={isContactDisabled()}
            isLoading={isLoadingIcon}
            buttonCopy={buttonCopy}
            withGradient={isFloatingChatEnabled}
            {...buttonProps}
            {...contactButtonProps}
            onClick={buttonProps.onClick}
        />
    );

    return (
        <Container
            width={fullWidth ? '100%' : undefined}
            height={fullHeight ? '100%' : undefined}
            className={className}
            ref={buttonRef}
        >
            {shouldRenderDropdown ? (
                <ContactOptions
                    buttonCopy={buttonCopy}
                    renderTrigger={renderTrigger ? () => renderTrigger({ onClick: noop }) : undefined}
                    onContactClick={onContactClick}
                    options={{
                        priorInteraction: modalInitialData?.priorInteraction,
                        source,
                        aiBriefInitialGenerationText,
                    }}
                    contactButtonProps={{ ...contactButtonProps, onClick: contactButtonProps?.onClick || noop }}
                    seller={seller}
                    gigs={gigs}
                />
            ) : (
                displayElement
            )}

            <UserActivationModalWrapper
                isOpen={activateUserModal}
                onClose={onCloseActivateModal}
                action={SUPPORTED_ACTIONS.CONTACT_SELLER}
            />
            <LazyComponent
                moduleName="ContactSellerDrawer"
                lazyImport={lazyImportLink}
                shouldImport={showModal}
                componentProps={modalProps}
            />
            {shouldRenderFloatingBtn && !isContactDisabled() && (
                <Container
                    display={{
                        default: 'none',
                        lg: 'inlineBlock',
                    }}
                    position="fixed"
                    left={theme.spacing['8']}
                    bottom={theme.spacing['8']}
                    zIndex="2"
                >
                    <ContactSellerButton
                        recipientInfo={{
                            username,
                            profilePhoto,
                            responseTimeInHrs,
                            isOnline,
                            displayName,
                        }}
                        onClick={onContactClick}
                        isAssistantActive={isFloatingChatEnabled}
                    />
                </Container>
            )}
        </Container>
    );
};

/** @type {typeof ContactButtonView} */
export const ContactButton = SafeWrapper(ContactButtonView, 'ContactButton');
